import { WidgetsConfigurations } from "./widgetsConfiguration";
import { type Layout } from "react-grid-layout";
import { type GRID_ITEM_PROPS } from "../types/gridLayout";

// Enhanced breakpoints with 4K support and better responsive design
export const breakpoints = {
  xxl: 2560, // 4K displays
  xl: 1920, // Full HD displays
  lg: 1200, // Large tablets/small desktops
  md: 996, // Tablets
  sm: 768, // Small tablets
  xs: 480, // Mobile landscape
  xxs: 0 // Mobile portrait
};

// Improved column configuration for better alignment
export const columns = {
  xxl: 24, // 4K displays - more columns for better space utilization
  xl: 16, // Full HD displays
  lg: 12, // Large tablets/small desktops
  md: 8, // Tablets - reduced from 10 for better alignment
  sm: 6, // Small tablets
  xs: 4, // Mobile landscape
  xxs: 2 // Mobile portrait
};

export type Breakpoint = keyof typeof breakpoints;
export type LayoutConfigurations = {
  [key in Breakpoint]?: Layout[];
};

export const getBreakpoint = (innerWidth: number): Breakpoint => {
  if (innerWidth >= breakpoints.xxl) {
    return "xxl";
  } else if (innerWidth >= breakpoints.xl) {
    return "xl";
  } else if (innerWidth >= breakpoints.lg) {
    return "lg";
  } else if (innerWidth >= breakpoints.md) {
    return "md";
  } else if (innerWidth >= breakpoints.sm) {
    return "sm";
  } else if (innerWidth >= breakpoints.xs) {
    return "xs";
  } else {
    return "xxs";
  }
};

export const widgetIdSplitSymbol = "*";

// Enhanced widget arrangement logic with better collision detection
export const arrangeWidgets = (
  layoutConfig: LayoutConfigurations,
  columnsConfig = columns
): LayoutConfigurations => {
  // Process each breakpoint
  Object.keys(breakpoints).forEach((bp) => {
    const breakpoint = bp as Breakpoint;
    if (!layoutConfig[breakpoint] || !layoutConfig[breakpoint]?.length) return;

    let currentRow = 0;
    let currentX = 0;
    let maxHeightInRow = 0;
    const cols = columnsConfig[breakpoint];

    // Sort widgets by their preferred position if they have one, otherwise by index
    const sortedWidgets = [...(layoutConfig[breakpoint] || [])].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });

    sortedWidgets.forEach((widget) => {
      // Ensure widget width doesn't exceed column limit
      widget.w = Math.min(widget.w, cols);

      // Check if widget can fit in the current row
      if (currentX + widget.w > cols) {
        // Move to the next row
        currentRow += maxHeightInRow;
        maxHeightInRow = 0;
        currentX = 0;
      }

      // Update widget position
      widget.x = currentX;
      widget.y = currentRow;

      // Prepare for the next widget
      currentX += widget.w;
      maxHeightInRow = Math.max(maxHeightInRow, widget.h);
    });

    // Update the layout with sorted widgets
    layoutConfig[breakpoint] = sortedWidgets;
  });

  return layoutConfig;
};

const getGridProps = (props: any[]): GRID_ITEM_PROPS => {
  const [[w, h], [minW, minH], [maxW, maxH], isResizable, isDraggable] = props;
  return {
    w,
    h,
    minW,
    minH,
    maxW,
    maxH,
    isResizable,
    isDraggable,
    resizeHandles: ["se"]
  };
};

const getLayoutProps = (layout: any[], bp: string): GRID_ITEM_PROPS => {
  const [xxl, xl, lg, md, sm, xs, xxs] = layout;
  switch (bp) {
    case "xxl":
      return getGridProps(xxl || xl || lg);
    case "xl":
      return getGridProps(xl || lg);
    case "lg":
      return getGridProps(lg);
    case "md":
      return getGridProps(md);
    case "sm":
      return getGridProps(sm);
    case "xs":
      return getGridProps(xs);
    case "xxs":
      return getGridProps(xxs);
    default:
      return getGridProps(lg);
  }
};

interface BpLayoutParams {
  wId: string;
  bp: string;
}

const getBpLayoutDims = ({ wId, bp }: BpLayoutParams): GRID_ITEM_PROPS => {
  const config = {
    w: 0,
    h: 0,
    minW: 0,
    minH: 0,
    maxW: 0,
    maxH: 0,
    isResizable: true,
    isDraggable: true,
    resizeHandles: ["se"]
  };
  const [url, type] = wId.split(widgetIdSplitSymbol);

  if (!type || !(type in WidgetsConfigurations)) {
    console.error(`Unknown visual type ${type} from widget ${url}`);
    return Object.assign(
      {},
      config,
      getLayoutProps(WidgetsConfigurations["UNKNOWN_TYPE"]?.layout || [], bp)
    );
  }

  return Object.assign({}, config, getLayoutProps((WidgetsConfigurations as any)[type].layout, bp));
};

/**
 * Create widgets layout configuration (x,y,w,h,minW,minH,maxW,maxH,resizable?,draggable?)
 * with default position x=0, y=0
 */
export const createLayoutConfiguration = ({
  bp = breakpoints,
  items = []
}: {
  bp: typeof breakpoints;
  items: string[];
}): LayoutConfigurations => {
  const config: LayoutConfigurations = {};

  Object.keys(bp).forEach((b) => {
    const breakpoint = b as Breakpoint;
    config[breakpoint] = items.map((item) => {
      const dims = getBpLayoutDims({ wId: item, bp: b });
      return {
        i: item,
        x: 0,
        y: 0,
        ...dims
      } as Layout;
    });
  });

  return config;
};

export const getLayoutConfigFromLS = (name: string): LayoutConfigurations | null => {
  try {
    const res = localStorage.getItem(name);
    if (!res || res === "null") {
      console.warn(`Layout configuration for ${name} not found in localStorage`);
      return null;
    }
    return JSON.parse(res);
  } catch (e) {
    console.error(`Error parsing layout configuration for ${name}:`, e);
    return null;
  }
};

// Order from largest to smallest viewport, matching react-grid-layout's
// preference of falling back to the next-largest breakpoint when a layout
// for a given breakpoint is missing.
const BREAKPOINTS_DESC: Breakpoint[] = ["xxl", "xl", "lg", "md", "sm", "xs", "xxs"];

/**
 * Backfills missing breakpoint entries in a layout config so that every
 * breakpoint present in `breakpoints` has a layout array. Missing entries
 * are derived from the next-larger present breakpoint, falling back to the
 * next-smaller one, mirroring react-grid-layout's own fallback logic.
 *
 * Why: layouts persisted to localStorage before xl/xxl breakpoints were
 * introduced lack those entries. Combined with mismatched cols/breakpoints
 * configs this can surface as
 * `ResponsiveReactGridLayout: cols entry for breakpoint xl is missing`.
 * Backfilling ensures viewports >1080p always render a valid layout.
 */
export const ensureAllBreakpointLayouts = (
  config: LayoutConfigurations | null | undefined
): LayoutConfigurations => {
  const result: LayoutConfigurations = {};
  if (config) {
    BREAKPOINTS_DESC.forEach((bp) => {
      if (config[bp] && config[bp]!.length) {
        result[bp] = config[bp]!.map((item) => ({ ...item }));
      }
    });
  }

  for (let i = 0; i < BREAKPOINTS_DESC.length; i++) {
    const bp = BREAKPOINTS_DESC[i];
    if (result[bp] && result[bp]!.length) continue;

    let source: Layout[] | undefined;
    for (let j = i - 1; j >= 0 && !source; j--) {
      if (result[BREAKPOINTS_DESC[j]]?.length) source = result[BREAKPOINTS_DESC[j]];
    }
    for (let j = i + 1; j < BREAKPOINTS_DESC.length && !source; j++) {
      if (result[BREAKPOINTS_DESC[j]]?.length) source = result[BREAKPOINTS_DESC[j]];
    }

    if (source) {
      result[bp] = source.map((item) => ({ ...item }));
    }
  }

  return result;
};

const DEFAULT_RESPONSIVE_COLS: { [key in Breakpoint]: number } = { ...columns };
const DEFAULT_RESPONSIVE_BREAKPOINTS: { [key in Breakpoint]: number } = { ...breakpoints };

/**
 * Returns a cols map with every breakpoint key guaranteed to be present.
 * Acts as a safety net so that `getColsFromBreakpoint` in react-grid-layout
 * always finds an entry for the current viewport.
 */
export const ensureColsHaveBreakpoints = (
  cols: { [key: string]: number } | undefined | null
): { [key in Breakpoint]: number } => {
  const result = { ...DEFAULT_RESPONSIVE_COLS };
  if (cols) {
    BREAKPOINTS_DESC.forEach((bp) => {
      if (typeof cols[bp] === "number") result[bp] = cols[bp];
    });
  }
  return result;
};

/**
 * Returns a breakpoints map aligned with the cols map. Any cols key without
 * a corresponding breakpoint entry is filled from the canonical defaults.
 */
export const ensureBreakpointsHaveAllKeys = (
  bps: { [key: string]: number } | undefined | null
): { [key in Breakpoint]: number } => {
  const result = { ...DEFAULT_RESPONSIVE_BREAKPOINTS };
  if (bps) {
    BREAKPOINTS_DESC.forEach((bp) => {
      if (typeof bps[bp] === "number") result[bp] = bps[bp];
    });
  }
  return result;
};

// Enhanced layout validation to prevent overlaps
export const validateLayout = (layout: Layout[], columns: number): Layout[] => {
  const occupiedCells = new Set<string>();

  return layout.map((widget) => {
    // Ensure widget dimensions are within bounds
    widget.w = Math.min(widget.w, columns);
    widget.x = Math.max(0, Math.min(widget.x, columns - widget.w));
    widget.y = Math.max(0, widget.y);

    // Check for overlaps and adjust if necessary
    let adjusted = false;
    for (let y = widget.y; y < widget.y + widget.h; y++) {
      for (let x = widget.x; x < widget.x + widget.w; x++) {
        const cellKey = `${x},${y}`;
        if (occupiedCells.has(cellKey)) {
          // Find next available position
          widget.y = y + 1;
          adjusted = true;
          break;
        }
      }
      if (adjusted) break;
    }

    // Mark cells as occupied
    for (let y = widget.y; y < widget.y + widget.h; y++) {
      for (let x = widget.x; x < widget.x + widget.w; x++) {
        occupiedCells.add(`${x},${y}`);
      }
    }

    return widget;
  });
};
